import React from 'react';
import { TabsContent } from '../../ui/tabs';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';

const PRItemDetailsTab = ({
  masterData, currentItem, activeItemIndex, handleItemChange, materialPlants, variant,
}) => (
  <TabsContent value="item-details">
    <Card>
      <CardHeader>
        <CardTitle>Item {currentItem?.item_number} - Details</CardTitle>
        <CardDescription>Master data, quantities, dates, and procurement references</CardDescription>
      </CardHeader>
      <CardContent>
        {currentItem && (
          <div className="space-y-6">
            {/* Master Data Section */}
            <div>
              <h3 className="text-md font-semibold mb-3 text-gray-700">Master Data</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Plant *</Label>
                  <select
                    value={currentItem.plant}
                    onChange={(e) => handleItemChange(activeItemIndex, 'plant', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    required
                  >
                    <option value="">Select Plant</option>
                    {(() => {
                      const materialPlantsForItem = materialPlants[activeItemIndex] || [];
                      if (currentItem.material_id && materialPlantsForItem.length > 0) {
                        const plantIds = materialPlantsForItem.map((p) => p.plant_id);
                        return masterData.plants
                          .filter((plant) => plantIds.includes(plant.plant_id))
                          .map((plant) => (
                            <option key={plant.plant_id} value={plant.plant_id}>{plant.plant_id} - {plant.plant_name}</option>
                          ));
                      }
                      return masterData.plants.map((plant) => (
                        <option key={plant.plant_id} value={plant.plant_id}>{plant.plant_id} - {plant.plant_name}</option>
                      ));
                    })()}
                  </select>
                </div>

                <div>
                  <Label>Storage Location</Label>
                  <select
                    value={currentItem.storage_location}
                    onChange={(e) => handleItemChange(activeItemIndex, 'storage_location', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Select Storage Location</option>
                    {masterData.storageLocations
                      ?.filter((sl) => !currentItem.plant || sl.plant_id === currentItem.plant)
                      .map((sloc) => (
                        <option key={sloc.storage_location_id} value={sloc.storage_location_id}>{sloc.storage_location_id} - {sloc.name}</option>
                      ))}
                  </select>
                </div>

                <div>
                  <Label>Purchasing Organization *</Label>
                  <select
                    value={currentItem.purchasing_organization}
                    onChange={(e) => handleItemChange(activeItemIndex, 'purchasing_organization', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    required
                  >
                    <option value="">Select</option>
                    {masterData.purchasingOrgs.map((po) => (
                      <option key={po.purchasing_org_id} value={po.purchasing_org_id}>{po.purchasing_org_id} - {po.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Purchasing Group *</Label>
                  <select
                    value={currentItem.purchasing_group}
                    onChange={(e) => handleItemChange(activeItemIndex, 'purchasing_group', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    required
                  >
                    <option value="">Select</option>
                    {masterData.purchasingGroups.map((pg) => (
                      <option key={pg.purchasing_group_id} value={pg.purchasing_group_id}>{pg.purchasing_group_id} - {pg.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Desired Vendor</Label>
                  <select
                    value={currentItem.desired_vendor}
                    onChange={(e) => handleItemChange(activeItemIndex, 'desired_vendor', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Select Vendor</option>
                    {masterData.vendors.map((vendor) => (
                      <option key={vendor.vendor_number} value={vendor.vendor_number}>{vendor.vendor_number} - {vendor.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Service Number</Label>
                  <Input
                    value={currentItem.service_number}
                    onChange={(e) => handleItemChange(activeItemIndex, 'service_number', e.target.value)}
                    maxLength={20} placeholder="For service items"
                  />
                </div>

                <div>
                  <Label>Valuation Type</Label>
                  <select
                    value={currentItem.valuation_type}
                    onChange={(e) => handleItemChange(activeItemIndex, 'valuation_type', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Select</option>
                    {masterData.valuationTypes.map((vt) => {
                      const vtId = typeof vt === 'object' ? vt.valuation_type_id : vt;
                      return <option key={vtId} value={vtId}>{vtId}</option>;
                    })}
                  </select>
                </div>

                <div>
                  <Label>Batch</Label>
                  <Input
                    value={currentItem.batch}
                    onChange={(e) => handleItemChange(activeItemIndex, 'batch', e.target.value)}
                    maxLength={10} placeholder="Batch number"
                  />
                </div>

                <div>
                  <Label>Agreement Number</Label>
                  <Input
                    value={currentItem.agreement_number}
                    onChange={(e) => handleItemChange(activeItemIndex, 'agreement_number', e.target.value)}
                    maxLength={10} placeholder="Contract/SA"
                  />
                </div>

                <div>
                  <Label>Info Record Number</Label>
                  <Input
                    value={currentItem.info_record_number}
                    onChange={(e) => handleItemChange(activeItemIndex, 'info_record_number', e.target.value)}
                    maxLength={10} placeholder="Info record"
                  />
                </div>

                <div>
                  <Label>Fixed Vendor</Label>
                  <select
                    value={currentItem.fixed_vendor}
                    onChange={(e) => handleItemChange(activeItemIndex, 'fixed_vendor', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Select Fixed Vendor</option>
                    {masterData.vendors.map((vendor) => (
                      <option key={vendor.vendor_number} value={vendor.vendor_number}>{vendor.vendor_number} - {vendor.name}</option>
                    ))}
                  </select>
                </div>

                {/* MRP Controller — Create uses dropdown, Edit uses free text */}
                <div>
                  <Label>MRP Controller</Label>
                  {variant === 'edit' ? (
                    <Input
                      value={currentItem.mrp_controller}
                      onChange={(e) => handleItemChange(activeItemIndex, 'mrp_controller', e.target.value)}
                      maxLength={3} placeholder="MRP Controller"
                    />
                  ) : (
                    <select
                      value={currentItem.mrp_controller}
                      onChange={(e) => handleItemChange(activeItemIndex, 'mrp_controller', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Select MRP Controller</option>
                      {masterData.mrpControllers
                        .filter((mc) => !currentItem.plant || mc.plant_id === currentItem.plant)
                        .map((mc) => (
                          <option key={mc.mrp_controller_id} value={mc.mrp_controller_id}>{mc.mrp_controller_id} - {mc.description}</option>
                        ))}
                    </select>
                  )}
                </div>

                <div>
                  <Label>Requisitioner</Label>
                  <Input
                    value={currentItem.requisitioner}
                    onChange={(e) => handleItemChange(activeItemIndex, 'requisitioner', e.target.value)}
                    maxLength={12} placeholder="Item requisitioner"
                  />
                </div>

                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={currentItem.contact_person}
                    onChange={(e) => handleItemChange(activeItemIndex, 'contact_person', e.target.value)}
                    maxLength={50} placeholder="Contact person for item"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Manufacturer Part Number</Label>
                  <Input
                    value={currentItem.manufacturer_part_number}
                    onChange={(e) => handleItemChange(activeItemIndex, 'manufacturer_part_number', e.target.value)}
                    maxLength={40} placeholder="Manufacturer part number"
                  />
                </div>
              </div>
            </div>

            {/* Additional Dates Section */}
            <div>
              <h3 className="text-md font-semibold mb-3 text-gray-700">Additional Dates</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Request Date</Label>
                  <Input type="date" value={currentItem.request_date} onChange={(e) => handleItemChange(activeItemIndex, 'request_date', e.target.value)} />
                </div>
                <div>
                  <Label>Release Date</Label>
                  <Input type="date" value={currentItem.release_date} onChange={(e) => handleItemChange(activeItemIndex, 'release_date', e.target.value)} />
                </div>
                <div>
                  <Label>Delivery Flexibility (days)</Label>
                  <Input type="number" min="0" value={currentItem.delivery_date_flexibility} onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date_flexibility', e.target.value)} placeholder="0" />
                </div>
              </div>
            </div>

            {/* Control Indicators Section */}
            <div>
              <h3 className="text-md font-semibold mb-3 text-gray-700">Control Indicators</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id={`gr_${activeItemIndex}`} checked={currentItem.goods_receipt_required} onChange={(e) => handleItemChange(activeItemIndex, 'goods_receipt_required', e.target.checked)} className="h-4 w-4" />
                  <Label htmlFor={`gr_${activeItemIndex}`} className="cursor-pointer">Goods Receipt Required</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id={`ir_${activeItemIndex}`} checked={currentItem.invoice_receipt_required} onChange={(e) => handleItemChange(activeItemIndex, 'invoice_receipt_required', e.target.checked)} className="h-4 w-4" />
                  <Label htmlFor={`ir_${activeItemIndex}`} className="cursor-pointer">Invoice Receipt Required</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id={`closed_${activeItemIndex}`} checked={currentItem.closed} onChange={(e) => handleItemChange(activeItemIndex, 'closed', e.target.checked)} className="h-4 w-4" />
                  <Label htmlFor={`closed_${activeItemIndex}`} className="cursor-pointer">Closed</Label>
                </div>
                <div>
                  <Label>Processing Status</Label>
                  <select
                    value={currentItem.item_processing_status}
                    onChange={(e) => handleItemChange(activeItemIndex, 'item_processing_status', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    {masterData.processingStatuses.map((ps) => (
                      <option key={ps.processing_status_id} value={ps.processing_status_id}>{ps.description}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Release Indicator</Label>
                  <select
                    value={currentItem.release_indicator}
                    onChange={(e) => handleItemChange(activeItemIndex, 'release_indicator', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    {masterData.releaseIndicators.map((ri) => (
                      <option key={ri.release_indicator_id} value={ri.release_indicator_id}>{ri.description}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Item Notes */}
            <div>
              <Label>Item Notes</Label>
              <textarea
                value={currentItem.notes}
                onChange={(e) => handleItemChange(activeItemIndex, 'notes', e.target.value)}
                maxLength={500} rows={3}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Item-specific notes"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  </TabsContent>
);

export default PRItemDetailsTab;
